import {
  CareChatMessage,
  CareChatConnectionStatus,
  CARE_CHAT_CONNECTION_STATUS,
  CARE_CHAT_SENDER_TYPE
} from '@web/types/careChat'

type CareChatState = {
  messages: CareChatMessage[]
  unreadCount: number
  connectionStatus: CareChatConnectionStatus
  conversationId: string | null
  chatId: string | null
  managerTypingExpiresAt: number | null
  lastError: string | null
  // Последнее входящее сообщение менеджера, ожидающее показа уведомления
  // (снекбара). Потребитель показывает снекбар и затем чистит это поле.
  incomingNotification: CareChatMessage | null
}

const state: CareChatState = {
  messages: [],
  unreadCount: 0,
  connectionStatus: CARE_CHAT_CONNECTION_STATUS.IDLE,
  conversationId: null,
  chatId: null,
  managerTypingExpiresAt: null,
  lastError: null,
  incomingNotification: null
}

function sortByTimestamp (messages: CareChatMessage[]): CareChatMessage[] {
  return [...messages].sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0))
}

function findMessageIndex (messages: CareChatMessage[], target: CareChatMessage): number {
  if (target.id != null) {
    const byId = messages.findIndex(m => m.id != null && m.id === target.id)
    if (byId !== -1) return byId
  }
  if (target.localUid) {
    const byLocal = messages.findIndex(m => m.localUid != null && m.localUid === target.localUid)
    if (byLocal !== -1) return byLocal
  }
  return -1
}

const getters = {
  messages: (s: CareChatState): CareChatMessage[] => s.messages,
  unreadCount: (s: CareChatState): number => s.unreadCount,
  connectionStatus: (s: CareChatState): CareChatConnectionStatus => s.connectionStatus,
  hasUnread: (s: CareChatState): boolean => s.unreadCount > 0,
  isManagerTyping: (s: CareChatState): boolean => {
    if (s.managerTypingExpiresAt == null) return false
    return s.managerTypingExpiresAt * 1000 > Date.now()
  },
  lastIncomingTimestamp: (s: CareChatState): number => {
    let last = 0
    for (const m of s.messages) {
      if (m.senderType === CARE_CHAT_SENDER_TYPE.MANAGER && (m.timestamp ?? 0) > last) {
        last = m.timestamp ?? 0
      }
    }
    return last
  },
  incomingNotification: (s: CareChatState): CareChatMessage | null => s.incomingNotification
}

const mutations = {
  setConnectionStatus (s: CareChatState, status: CareChatConnectionStatus): void {
    s.connectionStatus = status
  },
  setConversationContext (s: CareChatState, payload: { chatId: string | null; conversationId: string | null }): void {
    s.chatId = payload.chatId
    s.conversationId = payload.conversationId
  },
  setUnreadCount (s: CareChatState, count: number): void {
    s.unreadCount = Math.max(0, count)
  },
  resetUnreadCount (s: CareChatState): void {
    s.unreadCount = 0
  },
  setLastError (s: CareChatState, message: string | null): void {
    s.lastError = message
  },
  setManagerTyping (s: CareChatState, expiresAt: number | null): void {
    s.managerTypingExpiresAt = expiresAt
  },
  setIncomingNotification (s: CareChatState, message: CareChatMessage | null): void {
    s.incomingNotification = message
  },
  clearIncomingNotification (s: CareChatState): void {
    s.incomingNotification = null
  },
  receiveMessages (s: CareChatState, messages: CareChatMessage[]): void {
    const next = [...s.messages]
    for (const incoming of messages) {
      const idx = findMessageIndex(next, incoming)
      if (idx >= 0) {
        next[idx] = { ...next[idx], ...incoming }
      } else {
        next.push(incoming)
      }
    }
    s.messages = sortByTimestamp(next)
  },
  receiveMessage (s: CareChatState, message: CareChatMessage): void {
    const next = [...s.messages]
    const idx = findMessageIndex(next, message)
    if (idx >= 0) {
      next[idx] = { ...next[idx], ...message }
    } else {
      next.push(message)
    }
    s.messages = sortByTimestamp(next)
  },
  resetMessages (s: CareChatState): void {
    s.messages = []
  },
  logout (s: CareChatState): void {
    s.messages = []
    s.unreadCount = 0
    s.connectionStatus = CARE_CHAT_CONNECTION_STATUS.IDLE
    s.conversationId = null
    s.chatId = null
    s.managerTypingExpiresAt = null
    s.lastError = null
    s.incomingNotification = null
  }
}

const actions = {}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
// eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any
